import React, {memo, useEffect, useState} from "react";
import {Handle, Position, Node, useReactFlow} from "@xyflow/react";
import {Icon} from "@/components/ui/icon";
import {FlowNodeData, FlowNodeTypes} from "@/types/node";
import {generateId} from "@/utils/index";
import {getSettingValueByKey} from "@/utils/index";
import {FlowNodesData} from "./NodesData";
import NodeWithToolbar from "../components/NodeToolbar";
import {useSelectedElements} from "@/hooks/useSelectedElements";
import {cn} from "@/lib/utils";
import NodeContent from "../components/NodeContent";
import {ActionRecordModel} from "@/api/models";
import {cloneDeep} from "lodash";

export function createNode(blueprint: Partial<Node>): Node<FlowNodeData> {
  const {
    id = generateId(),
    type,
    data = {},
    measured,
    position = {x: 0, y: 0},
  } = blueprint;

  return {
    id,
    type,
    // Deep copy the data to avoid reference mutation
    data: cloneDeep(data),
    position,
    measured,
    ...(blueprint.type === FlowNodeTypes.ROOT
      ? {hidden: true, connectable: false}
      : {}),
  } as Node<FlowNodeData>;
}

export const getNodeTitle = (node: Node<FlowNodeData>) => {
  const nodeConfig =
    FlowNodesData[node.data.type as keyof typeof FlowNodesData];
  const blueprintData = nodeConfig.blueprint?.data;
  const actionData = getSettingValueByKey(node.data.settings ?? [], "action");
  const actionName =
    node.data.type === FlowNodeTypes.ACTION &&
    (actionData as ActionRecordModel)?.title
      ? (actionData as ActionRecordModel)?.title
      : null;

  return (
    actionName ||
    getSettingValueByKey(node.data.settings ?? [], "name") ||
    blueprintData?.name ||
    blueprintData?.settingsTitle ||
    "New Node"
  );
};

const WorkflowNode = ({
  id,
  data,
  showContext,
}: {
  id: string;
  data: FlowNodeData;
  showContext: boolean;
}) => {
  const {deleteElements, getNode} = useReactFlow();
  const {getSelectedNode} = useSelectedElements();

  const selectedNode = getSelectedNode();
  const isPlaceholderNode = data.type === FlowNodeTypes.PLACEHOLDER;
  const isNodeSelected = selectedNode?.id === id;
  const [isToolbarVisible, setIsToolbarVisible] = useState(isNodeSelected);
  const nodeConfig = FlowNodesData[data.type as keyof typeof FlowNodesData];

  const nodeName = getNodeTitle(getNode(id) as Node<FlowNodeData>);

  useEffect(() => {
    setIsToolbarVisible(isNodeSelected);
  }, [isNodeSelected]);

  if (!nodeConfig) {
    console.error(`No configuration found for node type: ${data.type}`);
    return null;
  }

  const borderColor = nodeConfig.styles?.borderColor || "var(--fb-border)";
  const borderColorHover =
    nodeConfig.styles?.borderColorHover || "var(--fb-primary-border-light-35)";
  const borderColorActive =
    nodeConfig.styles?.borderColorHover || "var(--fb-primary)";
  const shadowActiveColor =
    nodeConfig.styles?.borderColorActive || "var(--fb-primary-shadow-light-12)";
  const contextColor = nodeConfig.styles?.contextColor || "transparent";

  const nodeStyles = {
    backgroundColor: nodeConfig.styles?.backgroundColor || "var(--fb-muted)",
    "--node-border-color": borderColor,
    "--node-border-color-hover": borderColorHover,
    "--node-border-color-active": borderColorActive,
    "--node-shadow-active-color": shadowActiveColor,
    "--node-icon-background-color": nodeConfig.styles?.iconBackgroundColor,
    ...(showContext
      ? {
          "--overlay-color":
            contextColor.length > 0 ? contextColor : "transparent",
        }
      : {}),
  };

  return (
    <div
      onMouseEnter={() => setIsToolbarVisible(true)}
      onMouseLeave={() => !isNodeSelected && setIsToolbarVisible(false)}
    >
      <div
        className={cn(
          `node flex flex-col w-85 bg-white rounded-xl transition-all gap-2 p-2`,
          `border hover:border-[var(--node-border-color-hover)]`,
          isPlaceholderNode && "invisible pointer-events-none",
          showContext && "enableContext",
          `hover:bg-[${nodeConfig.styles?.backgroundColorActive || "var(--fb-muted)"}]`,
          isNodeSelected &&
            "shadow-[0px_0px_0px_6px_var(--node-shadow-active-color)] border-[var(--node-border-color-active)] hover:border-[var(--node-border-color-active)]",
        )}
        style={nodeStyles}
      >
        <div className="flex items-center gap-2 px-1 pt-1 pb-0.5">
          <Icon
            className={cn(
              "border-0 size-5 bg-[var(--node-icon-background-color)] [&_svg]:size-3 rounded",
            )}
          >
            {(nodeConfig.icon || nodeConfig.iconInverted) &&
              React.createElement(nodeConfig.iconInverted || nodeConfig.icon, {
                color: nodeConfig.styles?.inverted
                  ? nodeConfig.styles?.iconColor
                  : "white",
              })}
          </Icon>
          <div className="leading-none min-w-0">
            <h3
              className={cn(
                "text-sm font-medium hasContext hiddenContext overflow-hidden whitespace-nowrap text-ellipsis",
                nodeConfig.styles?.inverted && "text-white",
              )}
            >
              {nodeName.toString()}
            </h3>
          </div>
        </div>

        {!isPlaceholderNode && <NodeContent data={data} />}
        <NodeWithToolbar
          nodeId={id}
          nodeType={data.type}
          isToolbarVisible={isToolbarVisible}
          onDelete={() => {
            deleteElements({nodes: [{id}]});
          }}
          onMouseEnter={() => setIsToolbarVisible(true)}
          onMouseLeave={() => isNodeSelected && setIsToolbarVisible(false)}
        />
      </div>

      <Handle
        type="target"
        position={Position.Top}
        isConnectable={false}
        className={"handle"}
        id={`${id}-top`}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        className={"handle"}
        id={`${id}-bottom`}
      />
      <Handle
        type="source"
        position={Position.Right}
        isConnectable={true}
        className={"handle"}
        id={`${id}-right-source`}
      />
      <Handle
        type="source"
        position={Position.Left}
        isConnectable={true}
        className={"handle"}
        id={`${id}-left-source`}
      />
    </div>
  );
};

export default memo(WorkflowNode);
